import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllAccounts } from "../expense/accounts/AccountService";
import { getAllCategories } from "../expense/categories/CategoryService";
import { getAllMerchants } from "../expense/merchants/MerchantService";
import {
  acceptParsedMessage,
  getPendingParsedMessages,
  rejectParsedMessage,
} from "./IncomingMessageService";
import "./IncomingMessageReviewModal.css";

const REQUIRED_FIELDS = ["accountId", "transactionType", "amount", "transactionDate"];

const TYPE_OPTIONS = [
  { value: "DEBIT", label: "Debit (Expense)" },
  { value: "CREDIT", label: "Credit (Income)" },
];

export default function IncomingMessageReviewModal({ enabled, open, onClose, onCountChange }) {
  const navigate = useNavigate();
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [errors, setErrors] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!enabled) {
      setInternalOpen(false);
      setInitialized(false);
      setEntries([]);
      setSelectedId(null);
      return;
    }
    if (initialized) return;
    fetchEntries();
    fetchLookups();
  }, [enabled, initialized]);

  useEffect(() => {
    if (!isControlled) {
      if (entries.length > 0 && !internalOpen) {
        setInternalOpen(true);
      }
      if (entries.length === 0) {
        setInternalOpen(false);
      }
    }
    if (!selectedId && entries.length > 0) {
      setSelectedId(entries[0].id);
    }
  }, [entries, internalOpen, isControlled, selectedId]);

  useEffect(() => {
    if (typeof onCountChange === "function") {
      const holdCount = entries.filter((entry) => entry.status === "HOLD").length;
      onCountChange({ total: entries.length, hold: holdCount });
    }
  }, [entries, onCountChange]);

  useEffect(() => {
    if (entries.length === 0) return;
    setDrafts((prev) => {
      const next = { ...prev };
      entries.forEach((entry) => {
        if (!next[entry.id]) {
          next[entry.id] = buildDraft(entry);
        }
      });
      return next;
    });
  }, [entries]);

  const selectedEntry = useMemo(
    () => entries.find((entry) => entry.id === selectedId) || null,
    [entries, selectedId]
  );

  const selectedDraft = selectedEntry ? drafts[selectedEntry.id] : null;

  const categoryOptions = useMemo(() => {
    if (!selectedDraft?.transactionType) return categories;
    const want = selectedDraft.transactionType === "DEBIT" ? "expense" : "income";
    return categories.filter((c) => {
      if (!c.categoryType) return true;
      return c.categoryType.toLowerCase() === want;
    });
  }, [categories, selectedDraft?.transactionType]);

  const hasAccounts = accounts.length > 0;
  const holdReason = selectedEntry?.holdReason || selectedEntry?.holdComment;

  async function fetchEntries() {
    try {
      const data = await getPendingParsedMessages();
      setEntries(data);
      setInitialized(true);
    } catch (err) {
      setLoadError(err?.message || "Failed to load incoming messages.");
      setInitialized(true);
    }
  }

  async function fetchLookups() {
    try {
      const [acc, cat, mer] = await Promise.all([
        getAllAccounts(),
        getAllCategories(),
        getAllMerchants(),
      ]);
      setAccounts(acc);
      setCategories(cat);
      setMerchants(mer);
    } catch (err) {
      setLoadError(err?.message || "Failed to load finance lookups.");
    }
  }

  function buildDraft(entry) {
    return {
      accountId: entry.accountId || "",
      categoryId: entry.categoryId || "",
      categoryName: entry.categoryNameSuggested || "",
      merchantId: entry.merchantId || "",
      transactionType: entry.transactionType || "",
      amount: entry.amount ?? "",
      transactionDate: entry.transactionDate || "",
      currency: entry.currency || "INR",
      description: entry.description || "",
      merchantName: entry.merchantNameNormalized || entry.merchantNameRaw || "",
    };
  }

  function accountLabel(account) {
    if (!account) return "Account";
    const name = account.accountName || "Account";
    const rawNumber =
      account.accountNumber ||
      account.account_no ||
      account.accountNo ||
      account.accountNum ||
      "";
    const digits = String(rawNumber).replace(/[^0-9]/g, "");
    const last4 = digits.length >= 4 ? digits.slice(-4) : "";
    if (last4) return `${name} • ****${last4}`;
    if (rawNumber) return `${name} • ${rawNumber}`;
    return name;
  }

  function updateDraft(field, value) {
    if (!selectedEntry) return;
    setDrafts((prev) => ({
      ...prev,
      [selectedEntry.id]: {
        ...prev[selectedEntry.id],
        [field]: value,
      },
    }));
    setErrors((prev) => ({ ...prev, [selectedEntry.id]: {} }));
  }

  function validateDraft(draft) {
    const missing = {};
    REQUIRED_FIELDS.forEach((key) => {
      if (!draft[key] || `${draft[key]}`.trim() === "") {
        missing[key] = "Required";
      }
    });
    if (draft.merchantId === "__create__" && !draft.merchantName) {
      missing.merchantName = "Required";
    }
    if (draft.categoryId === "__create__" && !draft.categoryName) {
      missing.categoryName = "Required";
    }
    return missing;
  }

  async function handleAccept() {
    if (!selectedEntry || !selectedDraft) return;
    const validation = validateDraft(selectedDraft);
    if (Object.keys(validation).length > 0) {
      setErrors((prev) => ({ ...prev, [selectedEntry.id]: validation }));
      return;
    }
    setBusyId(selectedEntry.id);
    const payload = {
      accountId: selectedDraft.accountId,
      categoryId: selectedDraft.categoryId || null,
      categoryName: selectedDraft.categoryId === "__create__" ? selectedDraft.categoryName : null,
      merchantId: selectedDraft.merchantId || null,
      merchantName: selectedDraft.merchantId === "__create__" ? selectedDraft.merchantName : null,
      transactionType: selectedDraft.transactionType,
      currency: selectedDraft.currency || "INR",
      description: selectedDraft.description || selectedDraft.merchantName,
      amount: Number(selectedDraft.amount),
      transactionDate: selectedDraft.transactionDate,
    };
    try {
      const res = await acceptParsedMessage(selectedEntry.id, payload);
      if (!res.ok) {
        setLoadError("Failed to save transaction. Please try again.");
        return;
      }
      removeEntry(selectedEntry.id);
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject() {
    if (!selectedEntry) return;
    if (!window.confirm("Reject this transaction suggestion?")) return;
    setBusyId(selectedEntry.id);
    try {
      const res = await rejectParsedMessage(selectedEntry.id, {
        reason: "User rejected",
        comment: selectedEntry.holdComment || "",
      });
      if (!res.ok) {
        setLoadError("Failed to reject. Please try again.");
        return;
      }
      removeEntry(selectedEntry.id);
    } finally {
      setBusyId(null);
    }
  }

  function removeEntry(id) {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedId((prev) => {
      if (prev !== id) return prev;
      const remaining = entries.filter((entry) => entry.id !== id);
      return remaining[0]?.id || null;
    });
  }

  function handleClose() {
    if (isControlled && typeof onClose === "function") {
      onClose();
      return;
    }
    setInternalOpen(false);
  }

  const isOpen = isControlled ? open : internalOpen;

  if (!enabled || !isOpen) return null;

  return (
    <div className="incoming-review-overlay" role="dialog" aria-modal="true">
      <div className="incoming-review-modal">
        <div className="incoming-review-header">
          <div>
            <div className="incoming-review-title">Verify Incoming Transactions</div>
            <div className="incoming-review-subtitle">
              Review the parsed SMS entries before they are added to your transactions.
            </div>
          </div>
          <button className="incoming-review-close" onClick={handleClose} aria-label="Close">
            Close
          </button>
        </div>

        {loadError && <div className="incoming-review-error">{loadError}</div>}

        <div className="incoming-review-body">
          <div className="incoming-review-list">
            {entries.length === 0 && (
              <div className="incoming-review-empty">No incoming messages to verify.</div>
            )}
            {entries.map((entry) => {
              const isActive = entry.id === selectedId;
              const amount = entry.amount ?? "--";
              const merchantLabel =
                entry.merchantNameNormalized || entry.merchantNameRaw || "Unknown merchant";
              const statusLabel = entry.status || "PENDING";
              return (
                <button
                  key={entry.id}
                  className={`incoming-review-card ${isActive ? "active" : ""}`}
                  onClick={() => setSelectedId(entry.id)}
                >
                  <div className="incoming-review-card-top">
                    <div className="incoming-review-card-merchant">{merchantLabel}</div>
                    <span className={`incoming-review-badge incoming-review-badge-${statusLabel.toLowerCase()}`}>
                      {statusLabel}
                    </span>
                  </div>
                  <div className="incoming-review-card-meta">
                    <span>₹ {amount}</span>
                    <span>{entry.transactionType || "UNKNOWN"}</span>
                    <span>{entry.transactionDate || "No date"}</span>
                  </div>
                  {entry.holdReason && (
                    <div className="incoming-review-card-hold">{entry.holdReason}</div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="incoming-review-detail">
            {!selectedEntry || !selectedDraft ? (
              <div className="incoming-review-empty">Select a transaction to review.</div>
            ) : (
              <>
                <div className="incoming-review-section">
                  <div className="incoming-review-section-title">SMS Snapshot</div>
                  <div className="incoming-review-snapshot">
                    <div>{selectedEntry.bankName || "Bank"} • A/C {selectedEntry.accountLast4 || "----"}</div>
                    <div>{selectedEntry.referenceId ? `Ref ${selectedEntry.referenceId}` : "Reference unavailable"}</div>
                    {selectedEntry.upiId && <div>UPI: {selectedEntry.upiId}</div>}
                  </div>
                {holdReason && (
                  <div className="incoming-review-hold">
                    <strong>On hold:</strong> {selectedEntry.holdComment || selectedEntry.holdReason}
                  </div>
                )}
                {selectedEntry.holdReason === "ACCOUNT_NOT_FOUND" && (
                  <div className="incoming-review-warning">
                    Account not found. Please create the matching bank account to proceed.
                    <button
                      className="incoming-review-link"
                      type="button"
                      onClick={() => {
                        handleClose();
                        navigate("/finance/accounts");
                      }}
                    >
                      Create account
                    </button>
                  </div>
                )}
                </div>

                {!hasAccounts && (
                  <div className="incoming-review-warning">
                    No accounts found. Create a bank account before you accept this entry.
                    <button
                      className="incoming-review-link"
                      onClick={() => navigate("/finance/accounts")}
                    >
                      Create account
                    </button>
                  </div>
                )}

                <div className="incoming-review-form">
                  <label>
                    Account
                    <select
                      value={selectedDraft.accountId}
                      onChange={(e) => updateDraft("accountId", e.target.value)}
                      className={errors[selectedEntry.id]?.accountId ? "error" : ""}
                    >
                      <option value="">Select account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {accountLabel(account)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Type
                    <select
                      value={selectedDraft.transactionType}
                      onChange={(e) => updateDraft("transactionType", e.target.value)}
                      className={errors[selectedEntry.id]?.transactionType ? "error" : ""}
                    >
                      <option value="">Select type</option>
                      {TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Amount
                    <input
                      type="number"
                      step="0.01"
                      value={selectedDraft.amount}
                      onChange={(e) => updateDraft("amount", e.target.value)}
                      className={errors[selectedEntry.id]?.amount ? "error" : ""}
                    />
                  </label>

                  <label>
                    Date
                    <input
                      type="date"
                      value={selectedDraft.transactionDate}
                      onChange={(e) => updateDraft("transactionDate", e.target.value)}
                      className={errors[selectedEntry.id]?.transactionDate ? "error" : ""}
                    />
                  </label>

                  <label>
                    Merchant
                    <select
                      value={selectedDraft.merchantId}
                      onChange={(e) => updateDraft("merchantId", e.target.value)}
                    >
                      <option value="">Select merchant</option>
                      {merchants.map((merchant) => (
                        <option key={merchant.id} value={merchant.id}>
                          {merchant.merchantName}
                        </option>
                      ))}
                      <option value="__create__">Create new merchant</option>
                      {selectedDraft.merchantName && !merchants.some((m) => (m.merchantName || "").toLowerCase() === selectedDraft.merchantName.toLowerCase()) && (
                        <option value="__create__">Create: {selectedDraft.merchantName}</option>
                      )}
                    </select>
                  </label>

                  <label>
                    Category
                    <select
                      value={selectedDraft.categoryId}
                      onChange={(e) => updateDraft("categoryId", e.target.value)}
                    >
                      <option value="">Select category</option>
                      {categoryOptions.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.categoryName}
                        </option>
                      ))}
                      <option value="__create__">Create new category</option>
                      {selectedDraft.categoryName && !categoryOptions.some((c) => (c.categoryName || "").toLowerCase() === selectedDraft.categoryName.toLowerCase()) && (
                        <option value="__create__">Create: {selectedDraft.categoryName}</option>
                      )}
                    </select>
                  </label>

                  {selectedDraft.merchantId === "__create__" && (
                    <label>
                      New merchant name
                      <input
                        type="text"
                        value={selectedDraft.merchantName}
                        onChange={(e) => updateDraft("merchantName", e.target.value)}
                        className={errors[selectedEntry.id]?.merchantName ? "error" : ""}
                      />
                    </label>
                  )}

                  {selectedDraft.categoryId === "__create__" && (
                    <label>
                      New category name
                      <input
                        type="text"
                        value={selectedDraft.categoryName}
                        onChange={(e) => updateDraft("categoryName", e.target.value)}
                        className={errors[selectedEntry.id]?.categoryName ? "error" : ""}
                      />
                    </label>
                  )}

                  <label className="full">
                    Description
                    <input
                      type="text"
                      value={selectedDraft.description}
                      onChange={(e) => updateDraft("description", e.target.value)}
                      placeholder={selectedDraft.merchantName || "Description"}
                    />
                  </label>
                </div>

                <div className="incoming-review-actions">
                  <button
                    className="incoming-review-btn reject"
                    onClick={handleReject}
                    disabled={busyId === selectedEntry.id}
                  >
                    Reject
                  </button>
                  <button
                    className="incoming-review-btn accept"
                    onClick={handleAccept}
                    disabled={busyId === selectedEntry.id || !hasAccounts}
                  >
                    Accept & Save
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
